import {
	RiArrowDownSLine,
	RiArrowRightSLine,
	RiCheckboxLine,
	RiCodeSSlashLine,
	RiDoubleQuotesL,
	RiH1,
	RiH2,
	RiH3,
	RiListOrdered,
	RiListUnordered,
	RiTextBlock,
} from "@remixicon/react";
import type { TElement } from "platejs";
import { KEYS } from "platejs";
import { useEditorRef, useSelectionFragmentProp } from "platejs/react";
import * as React from "react";
import { getBlockType, setBlockType } from "@/components/editor/transforms";
import { cn } from "@/lib/utils";

const turnIntoItems = [
	{ icon: <RiTextBlock className="size-4" />, label: "Text", value: KEYS.p },
	{ icon: <RiH1 className="size-4" />, label: "Heading 1", value: "h1" },
	{ icon: <RiH2 className="size-4" />, label: "Heading 2", value: "h2" },
	{ icon: <RiH3 className="size-4" />, label: "Heading 3", value: "h3" },
	{
		icon: <RiListUnordered className="size-4" />,
		label: "Bulleted list",
		value: KEYS.ul,
	},
	{
		icon: <RiListOrdered className="size-4" />,
		label: "Numbered list",
		value: KEYS.ol,
	},
	{
		icon: <RiCheckboxLine className="size-4" />,
		label: "To-do list",
		value: KEYS.listTodo,
	},
	{
		icon: <RiArrowRightSLine className="size-4" />,
		label: "Toggle",
		value: KEYS.toggle,
	},
	{
		icon: <RiCodeSSlashLine className="size-4" />,
		label: "Code",
		value: KEYS.codeBlock,
	},
	{
		icon: <RiDoubleQuotesL className="size-4" />,
		label: "Quote",
		value: KEYS.blockquote,
	},
];

export function TurnIntoToolbarButton() {
	const editor = useEditorRef();
	const [open, setOpen] = React.useState(false);
	const ref = React.useRef<HTMLDivElement>(null);

	const value = useSelectionFragmentProp({
		defaultValue: KEYS.p,
		getProp: (node) => getBlockType(node as TElement),
	});

	const selectedItem = React.useMemo(
		() => turnIntoItems.find((item) => item.value === (value ?? KEYS.p)) ?? turnIntoItems[0],
		[value],
	);

	React.useEffect(() => {
		if (!open) return;
		function handleClickOutside(e: MouseEvent) {
			if (ref.current && !ref.current.contains(e.target as Node)) {
				setOpen(false);
			}
		}
		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, [open]);

	return (
		<div ref={ref} className="relative">
			<button
				type="button"
				className="inline-flex h-8 cursor-pointer items-center gap-1 rounded-md px-2 text-sm hover:bg-muted"
				onClick={() => setOpen(!open)}
				aria-pressed={open}
			>
				{selectedItem!.label}
				<RiArrowDownSLine className="size-3.5 text-muted-foreground" />
			</button>

			{open && (
				<div className="absolute top-full left-0 z-50 mt-1 min-w-[180px] rounded-md border bg-popover p-1 shadow-md">
					{turnIntoItems.map(({ icon, label, value: itemValue }) => (
						<button
							key={itemValue}
							type="button"
							className={cn(
								"flex w-full cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent",
								itemValue === value && "bg-accent",
							)}
							onClick={() => {
								setBlockType(editor, itemValue);
								setOpen(false);
								editor.tf.focus();
							}}
						>
							<span className="text-muted-foreground">{icon}</span>
							{label}
						</button>
					))}
				</div>
			)}
		</div>
	);
}
